import { Capacitor } from '@capacitor/core';
import { compiledVersion } from '../../../compiled-version';

export interface AuthDevice {
    type: 'geo:app' | 'geo:web' | 'geo:pwa';
    device_type: 'smartphone' | 'tablet' | 'desktop' | 'unknown';
    device_name?: string;
    app_build_at?: string;
}

// Chrome hides the Android model in the User-Agent ("K"), only Client Hints reveal it. Resolved long before a login.
let hintedModel: string | undefined;
(
    navigator as Navigator & { userAgentData?: { getHighEntropyValues(hints: string[]): Promise<{ model?: string }> } }
).userAgentData
    ?.getHighEntropyValues(['model'])
    .then(hints => (hintedModel = hints.model || undefined))
    .catch(() => {});

/**
 * Device profile sent with every login, stored on the auth session.
 */
export function getAuthDevice(): AuthDevice {
    const ua = navigator.userAgent;

    let device_type: AuthDevice['device_type'] = 'unknown';
    let device_name: string | undefined;

    // iPadOS Safari presents itself as a Mac by default, only touch support gives it away.
    const isIPad = ua.includes('iPad') || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    const isIPhone = ua.includes('iPhone') || ua.includes('iPod');
    const desktop = (
        [
            ['Windows', 'Windows'],
            ['Macintosh', 'macOS'],
            ['CrOS', 'ChromeOS'],
            ['Linux', 'Linux'],
        ] as const
    ).find(([needle]) => ua.includes(needle));

    if (isIPhone || isIPad) {
        device_type = isIPhone ? 'smartphone' : 'tablet';
        device_name = isIPhone ? 'Apple iPhone' : 'Apple iPad';
    } else if (ua.includes('Android')) {
        device_type = ua.includes('Mobile') ? 'smartphone' : 'tablet';
        // Firefox puts "Mobile" or "Tablet" where the model would be.
        const uaModel = ua.match(/Android [\d.]+; ([^;)]+)/)?.[1].replace(/ Build\/.*/, '');
        device_name = hintedModel ?? (['K', 'Mobile', 'Tablet'].includes(uaModel) ? 'Android' : (uaModel ?? 'Android'));
    } else if (desktop) {
        device_type = 'desktop';
        device_name = desktop[1];
    } else if (ua.includes('Mobi')) {
        device_type = 'smartphone';
    }

    // iOS Safari exposes a pinned home screen app only via navigator.standalone.
    const isPwa =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true;

    return {
        type: Capacitor.isNativePlatform() ? 'geo:app' : isPwa ? 'geo:pwa' : 'geo:web',
        device_type,
        ...(device_name && { device_name }),
        ...(compiledVersion.datetime && { app_build_at: compiledVersion.datetime }),
    };
}
